import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/http';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/dashboard/indicacoes')({ component: IndicacoesPage });

function IndicacoesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['referrals'],
    queryFn: () =>
      api.get('referrals/me').json<{
        code: string | null;
        totalReferred: number;
        activeReferrals: number;
        referrals: any[];
      }>(),
  });

  const link = data?.code ? `${window.location.origin}/register?ref=${data.code}` : null;
  const shareText = link
    ? `Conheça a Nosso Ello, plataforma para conectar profissionais e clientes: ${link}`
    : '';

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Indicações</h1>
        <p className="text-slate-500 text-sm mt-1">
          Compartilhe seu código com profissionais e clientes que possam se beneficiar da plataforma.
        </p>
      </div>

      {isLoading ? (
        <p className="text-slate-500 text-sm">Carregando...</p>
      ) : !data?.code ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="font-bold text-slate-700">Código de indicação não gerado</p>
            <p className="text-sm text-slate-500 mt-2">
              Entre em contato com o suporte para ativar seu programa.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <Kpi label="Total indicado" value={data.totalReferred} />
            <Kpi label="Com cadastro completo" value={data.activeReferrals} tone="teal" />
          </div>

          <Card>
            <CardContent className="p-5">
              <h3 className="font-bold text-slate-800">Seu código</h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Quem se cadastrar usando esse link já entra associado a você.
              </p>
              <div className="mt-4 flex items-stretch gap-2 flex-wrap">
                <div
                  className="flex-1 min-w-[260px] flex items-center justify-center rounded-xl text-white font-extrabold text-2xl tracking-[0.4em] py-4"
                  style={{ background: 'linear-gradient(135deg, #2E9B8B 0%, #237C6E 100%)' }}
                >
                  {data.code}
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigator.clipboard.writeText(data.code!)}
                  className="self-stretch"
                >
                  Copiar
                </Button>
              </div>

              {link && (
                <>
                  <div className="mt-5">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
                      Link completo
                    </div>
                    <div className="flex items-stretch gap-2 flex-wrap">
                      <div className="flex-1 min-w-[260px] bg-slate-50 rounded-md px-3 py-2 text-xs font-mono break-all border border-slate-200 flex items-center">
                        {link}
                      </div>
                      <Button variant="outline" onClick={() => navigator.clipboard.writeText(link)}>
                        Copiar
                      </Button>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2 flex-wrap">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                    >
                      <span aria-hidden>📱</span> WhatsApp
                    </a>
                    <a
                      href={`mailto:?subject=${encodeURIComponent('Conheça a Nosso Ello')}&body=${encodeURIComponent(shareText)}`}
                      className="inline-flex items-center gap-2 bg-coral-100 hover:bg-coral-200 text-coral-700 px-4 py-2 rounded-md text-sm font-medium"
                    >
                      <span aria-hidden>✉️</span> E-mail
                    </a>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h3 className="font-bold text-slate-800 mb-3">
                {data.totalReferred} pessoa{data.totalReferred === 1 ? '' : 's'} indicada{data.totalReferred === 1 ? '' : 's'}
              </h3>
              {data.referrals.length === 0 ? (
                <p className="text-sm text-slate-400">Ninguém usou seu link ainda.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.referrals.map((r: any) => (
                    <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {r.image ? (
                          <img src={r.image} alt="" className="size-9 rounded-full object-cover" />
                        ) : (
                          <div className="size-9 rounded-full bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white text-xs font-bold">
                            {(r.name ?? '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{r.name}</p>
                          <p className="text-[11px] text-slate-400 truncate">{r.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.hasProfile ? (
                          <span className="text-[10px] px-2 py-0.5 rounded border bg-teal-50 text-teal-700 border-teal-200">Cadastro completo</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">Em cadastro</span>
                        )}
                        <span className="text-[11px] text-slate-400">{new Date(r.createdAt).toLocaleDateString('pt-BR')}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone?: 'teal' }) {
  const cls = tone === 'teal' ? 'border-teal-100 bg-teal-50/40 text-teal-900' : 'border-slate-200 bg-white text-slate-900';
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70 font-semibold">{label}</div>
      <div className="text-2xl font-extrabold mt-1 tabular-nums">{value}</div>
    </div>
  );
}
